import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AnyAction } from "redux";
import { ProductService } from "$services";
import useAuth from "$hooks/useAuth";
import useIsProductDeletable from "$hooks/useIsProductDeletable";
import useReferencingCategory from "$hooks/useReferencingCategory";

export const useProductForm = (
  product?: AnyAction,
  company?: any,
  isReferenced = true,
  defaultValuesForm?: any
) => {
  const { isAdmin } = useAuth();
  const { isDeletable } = useIsProductDeletable();
  const { referencingCategory } = useReferencingCategory(company?._id);

  const isAReceptionRefProduct = product?.categories.some(
    (c) => c === referencingCategory?.id
  );

  const [canEditRefP, setCanEditRefP] = useState(false);

  const checkIsDeletable = useCallback(async () => {
    const res = await isDeletable(product?.reference, company?._id);
    setCanEditRefP(res);
  }, [company, isDeletable, product?.reference]);

  useEffect(() => {
    if (product?.reference) {
      checkIsDeletable();
    } else {
      setCanEditRefP(true);
    }
  }, [checkIsDeletable, product?.reference]);

  const defaultValues: any = product
    ? {
        ...product,
        reference: product.reference,
        secondReference: product.secondReference,
        designation: product.designation,
        unit: product.unit,
        description: product.description,
        substitutionProducts: product.substitutionProducts,
        categories: product.categories.map((c) => c._id),
        company: product.company,
        supplier: product.supplier,
      }
    : {
        id: null,
        company: company?._id,
        reference: defaultValuesForm?.reference || null,
        secondReference: null,
        designation: null,
        unit: null,
        description: null,
        substitutionProducts: null,
        categories: null,
        isReferenced,
        quantity: 0,
        supplier: null,
      };

  const form = useForm({
    mode: "onChange",
    defaultValues,
  });

  const { watch, reset } = form;

  const currentCompany = watch("company", defaultValues.company);

  // Reset form when defaultValuesForm.reference changes (e.g., prefilled reference)
  // This is a backup in case the component doesn't remount with a new key
  useEffect(() => {
    if (!product && defaultValuesForm?.reference) {
      reset((prevValues) => ({
        ...prevValues,
        reference: defaultValuesForm.reference,
      }));
    }
  }, [defaultValuesForm?.reference, product, reset]);

  const validateReference = async (value: string, companyId: string) => {
    if (!value?.length) {
      return null;
    }

    const checkProduct = await ProductService.getProductByReference({
      company: companyId,
      reference: value,
    });

    // update mode
    const filteredProducts = checkProduct.filter(
      (existingProduct) => existingProduct._id !== product?._id
    );

    if (filteredProducts.length > 0) {
      return "Cette référence existe déjà";
    }

    return null;
  };

  return {
    form,
    isAdmin: isAdmin(),
    canEditRefP,
    isAReceptionRefProduct,
    currentCompany,
    validateReference,
  };
};
